import React, { useState, useEffect, useCallback } from 'react';
import { MdViewList, MdAdd, MdDelete, MdContentCopy, MdEdit, MdClose, MdArrowUpward, MdArrowDownward, MdPublic } from 'react-icons/md';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { confirm } from '../lib/dialogs';
import SearchableSelect from '../components/SearchableSelect';

const Toast = ({ msg, type, onClose }) => (
  <div className={`toast toast-${type}`} style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}>
    {msg}<button onClick={onClose} style={{ marginLeft: 12, background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 16 }}>×</button>
  </div>
);

const FIELD_TYPES = [
  ['text', 'Text'], ['textarea', 'Long text'], ['number', 'Number'], ['date', 'Date'],
  ['dropdown', 'Dropdown (fixed list)'], ['master_dropdown', 'Master dropdown'],
  ['general_name', 'Asset General Name'], ['category_auto', 'Category (auto)'],
  ['category', 'Category'],
  ['serial', 'Serial No'], ['rfid', 'RFID Tag'], ['qr', 'QR Code'], ['barcode', 'Barcode'],
  ['location', 'Location'], ['status', 'Status'], ['photo', 'Photo'],
  ['image_url', 'Image link (URL)'],
  ['auto_timestamp', 'Timestamp (auto)'], ['auto_gps', 'GPS (auto)'], ['auto_assetid', 'Asset ID (auto)'],
];

const Masters = () => {
  const { user, hasPermission } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isSuperAdmin = isAdmin && !!user?.tenant?.is_platform_owner;
  // A non-admin can be granted template-field editing without being handed the
  // rest of Masters. Such a user sees only the Registration Templates tab —
  // master lists and general names remain an admin concern.
  const canEditTemplates = isAdmin || hasPermission('template-fields-edit');
  const tabs = isAdmin
    ? [['lists', 'Master Lists'], ['general', 'Asset General Names'], ['templates', 'Registration Templates']]
    : [['templates', 'Registration Templates']];
  const [tab, setTab] = useState(isAdmin ? 'lists' : 'templates');
  const [toast, setToast] = useState(null);
  const show = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  return (
    <div className="page-container">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
      <div className="page-header">
        <div>
          <div className="page-title"><MdViewList /> Masters</div>
          <div className="page-subtitle">Manage lookup libraries and registration templates</div>
        </div>
      </div>
      <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {tabs.map(([id, label]) => (
          <button key={id} className={`btn btn-sm ${tab === id ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === 'lists' && <MasterLists isAdmin={isAdmin} show={show} />}
      {tab === 'general' && <GeneralNames isSuperAdmin={isSuperAdmin} show={show} />}
      {tab === 'templates' && <Templates isAdmin={isAdmin} canEdit={canEditTemplates} show={show} />}
    </div>
  );
};

// ── Master Lists ─────────────────────────────────────────────────────────────
const MasterLists = ({ isAdmin, show }) => {
  const [masters, setMasters] = useState([]);
  const [newItem, setNewItem] = useState({});   // masterId -> text
  const [newMaster, setNewMaster] = useState({ name: '', key: '' });
  const load = useCallback(() => api.get('/masters').then(r => setMasters(r.data || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const addItem = async (m) => {
    const value = (newItem[m.id] || '').trim(); if (!value) return;
    try { await api.post(`/masters/${m.id}/items`, { value }); setNewItem(s => ({ ...s, [m.id]: '' })); load(); }
    catch (e) { show(e.response?.data?.error || 'Failed', 'error'); }
  };
  const delItem = async (id) => { try { await api.delete(`/masters/items/${id}`); load(); } catch (e) { show(e.response?.data?.error || 'Failed', 'error'); } };
  const addMaster = async () => {
    if (!newMaster.name.trim()) return;
    try { await api.post('/masters', { name: newMaster.name, key: newMaster.key || newMaster.name }); setNewMaster({ name: '', key: '' }); load(); }
    catch (e) { show(e.response?.data?.error || 'Failed', 'error'); }
  };
  const delMaster = async (m) => { if (!(await confirm(`Delete master "${m.name}" and its items?`))) return; try { await api.delete(`/masters/${m.id}`); load(); } catch (e) { show(e.response?.data?.error || 'Failed', 'error'); } };

  return (
    <>
      {isAdmin && (
        <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div><label className="form-label">New master list</label>
            <input className="form-control" placeholder="e.g. Manufacturer" value={newMaster.name} onChange={e => setNewMaster(s => ({ ...s, name: e.target.value }))} /></div>
          <button className="btn btn-primary" onClick={addMaster}><MdAdd /> Add list</button>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
        {masters.map(m => (
          <div className="card" key={m.id}>
            <div className="card-header">
              <div className="card-title">{m.name}
                {m.allow_user_add ? <span className="badge badge-info" style={{ marginLeft: 8 }}>user-add</span> : <span className="badge" style={{ marginLeft: 8, background: '#eee' }}>fixed</span>}
              </div>
              {isAdmin && <button className="btn btn-outline btn-sm" title="Delete list" onClick={() => delMaster(m)}><MdDelete /></button>}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {m.items.length === 0 && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>No values yet</span>}
              {m.items.map(it => (
                <span key={it.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f4eef3', borderRadius: 14, padding: '3px 6px 3px 10px', fontSize: 12 }}>
                  {it.value}
                  {isAdmin && <button onClick={() => delItem(it.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 14, lineHeight: 1 }}>×</button>}
                </span>
              ))}
            </div>
            {isAdmin && (
              <div style={{ display: 'flex', gap: 6 }}>
                <input className="form-control" placeholder="Add value…" value={newItem[m.id] || ''}
                  onChange={e => setNewItem(s => ({ ...s, [m.id]: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && addItem(m)} />
                <button className="btn btn-outline btn-sm" onClick={() => addItem(m)}><MdAdd /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

// ── Asset General Names (global) ─────────────────────────────────────────────
const GeneralNames = ({ isSuperAdmin, show }) => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: '', category: '' });
  const load = useCallback(() => api.get('/general-names').then(r => setRows(r.data || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!form.name.trim() || !form.category.trim()) return;
    try { await api.post('/general-names', form); setForm({ name: '', category: '' }); load(); }
    catch (e) { show(e.response?.data?.error || 'Failed', 'error'); }
  };
  const del = async (r) => { if (!(await confirm(`Delete "${r.name}"?`))) return; try { await api.delete(`/general-names/${r.id}`); load(); } catch (e) { show(e.response?.data?.error || 'Failed', 'error'); } };
  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title"><MdPublic style={{ verticalAlign: -3, marginRight: 6 }} />Asset General Names <span className="badge badge-info" style={{ marginLeft: 8 }}>shared</span></div>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: -6 }}>
        A single library shared across all companies. Picking a general name during registration auto-fills its category.
        {!isSuperAdmin && ' Only the platform owner can edit this list.'}
      </p>
      {isSuperAdmin && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12, flexWrap: 'wrap' }}>
          <div><label className="form-label">General name</label><input className="form-control" placeholder="e.g. Laptop" value={form.name} onChange={e => setForm(s => ({ ...s, name: e.target.value }))} /></div>
          <div><label className="form-label">Category</label><input className="form-control" placeholder="e.g. Computer" value={form.category} onChange={e => setForm(s => ({ ...s, category: e.target.value }))} /></div>
          <button className="btn btn-primary" onClick={add}><MdAdd /> Add</button>
        </div>
      )}
      <div className="table-container">
        <table>
          <thead><tr><th>General Name</th><th>Category</th>{isSuperAdmin && <th></th>}</tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={3} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>No entries yet</td></tr>
              : rows.map(r => <tr key={r.id}><td style={{ fontWeight: 500 }}>{r.name}</td><td>{r.category}</td>
                {isSuperAdmin && <td><button className="btn btn-outline btn-sm" onClick={() => del(r)}><MdDelete /></button></td>}</tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Registration Templates (list + field builder) ───────────────────────────
const Templates = ({ isAdmin, canEdit, show }) => {
  const [templates, setTemplates] = useState([]);
  const [masters, setMasters] = useState([]);
  const [editing, setEditing] = useState(null); // template being edited (or new)
  const load = useCallback(() => api.get('/templates').then(r => setTemplates(r.data || [])).catch(() => {}), []);
  useEffect(() => { load(); api.get('/masters').then(r => setMasters(r.data || [])).catch(() => {}); }, [load]);

  const clone = async (t) => { try { const r = await api.post(`/templates/${t.id}/clone`, {}); load(); setEditing(r.data); } catch (e) { show(e.response?.data?.error || 'Failed', 'error'); } };
  const del = async (t) => { if (!(await confirm(`Delete template "${t.name}"?`))) return; try { await api.delete(`/templates/${t.id}`); load(); } catch (e) { show(e.response?.data?.error || 'Failed', 'error'); } };
  const startNew = () => setEditing({ id: null, name: '', code_prefix: '', description: '', is_builtin: false, fields: [] });

  return (
    <>
      {isAdmin && (
        <div className="card" style={{ marginBottom: 16 }}>
          <button className="btn btn-primary" onClick={startNew}><MdAdd /> New template</button>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        {templates.map(t => (
          <div className="card" key={t.id}>
            <div className="card-header">
              <div className="card-title">{t.name} {t.is_builtin && <span className="badge" style={{ marginLeft: 6, background: '#eee' }}>built-in</span>}</div>
              <code style={{ fontSize: 11, color: 'var(--accent)' }}>{t.code_prefix}</code>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>{t.fields.length} fields · {t.description || '—'}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {canEdit && <button className="btn btn-outline btn-sm" onClick={() => setEditing(t)}><MdEdit /> {t.is_builtin ? 'View / Edit' : 'Edit'}</button>}
              {isAdmin && <button className="btn btn-outline btn-sm" onClick={() => clone(t)}><MdContentCopy /> Clone</button>}
              {isAdmin && !t.is_builtin && <button className="btn btn-outline btn-sm" onClick={() => del(t)}><MdDelete /></button>}
            </div>
          </div>
        ))}
      </div>
      {editing && <TemplateEditor template={editing} masters={masters} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} show={show} />}
    </>
  );
};

// Only a field an operator picks or types can be the asset's name — a photo,
// timestamp or GPS fix produces a "name" nobody can read.
const NAME_FIELD_TYPES = ['text', 'textarea', 'dropdown', 'master_dropdown', 'general_name'];

const TemplateEditor = ({ template, masters, onClose, onSaved, show }) => {
  const [name, setName] = useState(template.name);
  const [prefix, setPrefix] = useState(template.code_prefix || '');
  const [fields, setFields] = useState(template.fields.map(f => ({ ...f })));
  const [saving, setSaving] = useState(false);

  const upd = (i, patch) => setFields(fs => fs.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  const move = (i, d) => setFields(fs => { const n = [...fs]; const j = i + d; if (j < 0 || j >= n.length) return fs; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const addField = () => setFields(fs => {
    // Generate a key guaranteed unique against existing field_keys. Using
    // `fs.length + 1` alone collides after a field is removed (length no longer
    // maps to a free suffix), producing two fields with the same key.
    const used = new Set(fs.map(f => f.field_key));
    let n = fs.length + 1, k = `field_${n}`;
    while (used.has(k)) k = `field_${++n}`;
    return [...fs, { field_key: k, label: 'New Field', field_type: 'text', required: false, master_key: null }];
  });
  const removeField = (i) => setFields(fs => fs.filter((_, idx) => idx !== i));

  // Exactly one field names the asset — picking a new one clears the old, the
  // same way a radio group behaves. Clicking the current pick clears it and
  // hands the choice back to the backend's key/label/type fallback.
  const pickNameField = (i) => setFields(fs => fs.map((f, idx) => ({ ...f, is_name: idx === i ? !f.is_name : false })));

  // Turn a fixed-list ('dropdown') field's editor text into a clean options
  // array at save time. During editing the raw text is held in `optionsText`
  // (so typing commas/spaces isn't fought); non-dropdown fields keep their
  // existing options untouched.
  const optionsFor = (f) => {
    if (f.field_type !== 'dropdown') return f.options || null;
    const raw = f.optionsText != null
      ? f.optionsText
      : (Array.isArray(f.options) ? f.options.join(', ') : (f.options || ''));
    const arr = String(raw).split(',').map(s => s.trim()).filter(Boolean);
    return arr.length ? arr : null;
  };

  const save = async () => {
    if (!name.trim()) { show('Name is required', 'error'); return; }
    setSaving(true);
    const payload = { name, code_prefix: prefix, fields: fields.map(f => ({ field_key: f.field_key, label: f.label, field_type: f.field_type, required: !!f.required, master_key: f.master_key || null, options: optionsFor(f), is_name: !!f.is_name && NAME_FIELD_TYPES.includes(f.field_type) })) };
    try {
      if (template.id) await api.put(`/templates/${template.id}`, payload);
      else await api.post('/templates', payload);
      onSaved();
    } catch (e) { show(e.response?.data?.error || 'Save failed', 'error'); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9998, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: 24 }}>
      <div className="card" style={{ width: '100%', maxWidth: 760, marginTop: 20 }}>
        <div className="card-header">
          <div className="card-title">{!template.id ? 'New template' : (template.is_builtin ? 'View / edit built-in template' : 'Edit template')}</div>
          <button className="btn btn-outline btn-sm" onClick={onClose}><MdClose /></button>
        </div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 2, minWidth: 200 }}><label className="form-label">Name {template.is_builtin && <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(built-in — name locked)</span>}</label><input className="form-control" value={name} onChange={e => setName(e.target.value)} readOnly={template.is_builtin} style={template.is_builtin ? { background: '#f6f2f6' } : undefined} /></div>
          <div style={{ flex: 1, minWidth: 120 }}><label className="form-label">Asset ID prefix</label><input className="form-control" value={prefix} onChange={e => setPrefix(e.target.value)} placeholder="e.g. FF" /></div>
        </div>
        <label className="form-label">Fields</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          {fields.map((f, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.4fr 1.2fr auto auto auto', gap: 6, alignItems: 'center', background: '#faf7fb', padding: 6, borderRadius: 8 }}>
              <input className="form-control" value={f.label} placeholder="Label" onChange={e => upd(i, { label: e.target.value, field_key: f.field_key })} />
              <SearchableSelect className="form-control" value={f.field_type} onChange={e => upd(i, { field_type: e.target.value })}>
                {FIELD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </SearchableSelect>
              {f.field_type === 'master_dropdown'
                ? <SearchableSelect className="form-control" value={f.master_key || ''} onChange={e => upd(i, { master_key: e.target.value })}>
                    <option value="">— master —</option>{masters.map(m => <option key={m.key} value={m.key}>{m.name}</option>)}
                  </SearchableSelect>
                : f.field_type === 'dropdown'
                  ? <input className="form-control" placeholder="Options (comma-separated)"
                      value={f.optionsText != null ? f.optionsText : (Array.isArray(f.options) ? f.options.join(', ') : '')}
                      onChange={e => upd(i, { optionsText: e.target.value })} />
                  : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{f.field_key}</span>}
              <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}><input type="checkbox" checked={!!f.required} onChange={e => upd(i, { required: e.target.checked })} /> req</label>
              <label
                style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 3, opacity: NAME_FIELD_TYPES.includes(f.field_type) ? 1 : 0.35 }}
                title="This field is the asset's name — it shows on the asset card, the detail page and every export, and can be changed from there."
              >
                <input
                  type="radio"
                  name="tpl-name-field"
                  checked={!!f.is_name}
                  disabled={!NAME_FIELD_TYPES.includes(f.field_type)}
                  onChange={() => pickNameField(i)}
                  onClick={() => { if (f.is_name) pickNameField(i); }}
                /> name
              </label>
              <div style={{ display: 'flex', gap: 2 }}>
                <button className="btn btn-outline btn-sm" onClick={() => move(i, -1)} title="Up"><MdArrowUpward /></button>
                <button className="btn btn-outline btn-sm" onClick={() => move(i, 1)} title="Down"><MdArrowDownward /></button>
                <button className="btn btn-outline btn-sm" onClick={() => removeField(i)} title="Remove"><MdDelete /></button>
              </div>
            </div>
          ))}
        </div>
        <button className="btn btn-outline btn-sm" onClick={addField}><MdAdd /> Add field</button>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save template'}</button>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default Masters;
